import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  localItemsSaleSummary,
  localPartySaleSummary,
  localPurchaseRegister,
  localPurchaseSummary,
  localRecoveryReceipts,
  localSaleRegister,
  localSaleSummary,
  localServicesAnalysis,
  localSupplierSummary,
} from '@/offline';

type Tab =
  | 'sale-register'
  | 'sale-summary'
  | 'party'
  | 'items'
  | 'services'
  | 'recovery'
  | 'purchase-register'
  | 'purchase-summary'
  | 'suppliers';

const TABS: { id: Tab; label: string }[] = [
  { id: 'sale-register', label: 'Sale Register' },
  { id: 'sale-summary', label: 'Sale Summary' },
  { id: 'party', label: 'Party Summary' },
  { id: 'items', label: 'Items' },
  { id: 'services', label: 'Services' },
  { id: 'recovery', label: 'Recovery' },
  { id: 'purchase-register', label: 'Purchase Register' },
  { id: 'purchase-summary', label: 'Purchase Summary' },
  { id: 'suppliers', label: 'Suppliers' },
];

function today() {
  return new Date().toISOString().slice(0, 10);
}

function monthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export default function LocalOfflineReports() {
  const [tab, setTab] = useState<Tab>('sale-register');
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(today());

  const { data, isLoading } = useQuery({
    queryKey: ['localOfflineReports', tab, fromDate, toDate],
    queryFn: async () => {
      switch (tab) {
        case 'sale-register':
          return localSaleRegister(fromDate, toDate);
        case 'sale-summary':
          return localSaleSummary(fromDate, toDate);
        case 'party':
          return localPartySaleSummary(fromDate, toDate);
        case 'items':
          return localItemsSaleSummary(fromDate, toDate);
        case 'services':
          return localServicesAnalysis(fromDate, toDate);
        case 'recovery':
          return localRecoveryReceipts(fromDate, toDate);
        case 'purchase-register':
          return localPurchaseRegister(fromDate, toDate);
        case 'purchase-summary':
          return localPurchaseSummary(fromDate, toDate);
        case 'suppliers':
          return localSupplierSummary(fromDate, toDate);
        default:
          return [];
      }
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="text-xs text-gray-500">From</label>
          <input type="date" className="input-field" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-gray-500">To</label>
          <input type="date" className="input-field" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-2.5 py-1 text-xs rounded border ${tab === t.id ? 'bg-primary-50 border-primary-300 text-primary-800' : 'bg-white border-gray-200'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading local report…</p>
      ) : (
        <pre className="text-xs bg-white border rounded-lg p-3 overflow-auto max-h-[60vh]">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}
